import React, { createContext, useContext, useState, useEffect } from 'react';
import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Complete Translations - EN & FR
const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        home: 'Home',
        products: 'Products',
        pricing: 'Pricing',
        features: 'Features',
        howItWorks: 'How It Works',
        faq: 'FAQ',
        contact: 'Contact',
        login: 'Login',
        register: 'Get Started',
        dashboard: 'Dashboard',
        logout: 'Logout'
      },
      // Hero
      hero: {
        title: 'Transform your first impression into a powerful digital experience',
        subtitle: 'Share your contact details, links, and brand instantly with a single tap. No apps. No paper. Just impact.',
        cta: 'Get Your Card',
        ctaSecondary: 'See How It Works'
      },
      // Value Proposition
      value: {
        title: 'Why professionals choose IKATone',
        items: [
          'Impress clients instantly',
          'Share everything in one tap',
          'Never run out of business cards',
          'Update your information anytime',
          'Track your interactions'
        ]
      },
      // Features
      features: {
        title: 'Why Choose IKATone?',
        subtitle: 'Premium technology meets elegant design',
        instant: {
          title: 'Instant Sharing',
          description: 'Share your contact with a single tap. No app required for recipients.'
        },
        customizable: {
          title: 'Fully Customizable',
          description: 'Create your perfect digital profile with photos, links, and social media.'
        },
        analytics: {
          title: 'Track Connections',
          description: 'See how many times your profile has been viewed and shared.'
        },
        eco: {
          title: 'Eco-Friendly',
          description: 'One card replaces thousands of paper cards. Better for the planet.'
        },
        secure: {
          title: 'Secure & Private',
          description: 'Control what you share. Update or hide your info anytime.'
        },
        universal: {
          title: 'Universal Compatibility',
          description: 'Works with all modern smartphones. iOS and Android supported.'
        },
        realtime: {
          title: 'Real-Time Updates',
          description: 'Change your information anytime. Your card always shows the latest version.'
        },
        team: {
          title: 'Team Management',
          description: 'Manage multiple cards and profiles from a single dashboard.'
        },
        multilang: {
          title: 'Multilingual',
          description: 'Interface available in English and French. Reach a wider audience.'
        }
      },
      // Features Page (redesigned)
      featuresPage: {
        hero: {
          title: 'Everything you need to make a powerful impression',
          subtitle: 'Share your contact, links, and brand instantly with a modern digital experience designed for professionals.',
          cta: 'Explore Features'
        },
        core: {
          title: 'Core Features',
          items: [
            { title: 'Instant Contact Sharing', desc: 'Share your details instantly in one simple action' },
            { title: 'Custom Profile', desc: 'Create a professional profile that reflects your brand' },
            { title: 'Unlimited Updates', desc: 'Update your information anytime without limits' },
            { title: 'Social Links Integration', desc: 'Add all your social media and links in one place' },
            { title: 'Personal Dashboard', desc: 'Manage everything from your personal dashboard' },
            { title: 'Analytics Tracking', desc: 'Track interactions and engagement' }
          ]
        },
        benefits: {
          title: 'Why it matters',
          items: [
            'Make a strong first impression',
            'Stand out from competitors',
            'Simplify your networking',
            'Save time and money',
            'Always stay up to date'
          ]
        },
        useCases: {
          title: 'Perfect for',
          items: ['Entrepreneurs', 'Sales professionals', 'Business owners', 'Teams and companies', 'Creators and influencers']
        },
        howItWorks: {
          title: 'How It Works',
          steps: ['Get your card', 'Create your profile', 'Share instantly', 'Grow your network']
        },
        differentiation: 'More than a card. It\'s your digital identity.',
        socialProof: {
          title: 'What people say',
          reviews: ['Simple, powerful, and professional', 'Perfect for modern networking']
        },
        finalCta: {
          title: 'Start building stronger connections today',
          cta: 'Get Your Card'
        }
      },
      // Products
      products: {
        title: 'Choose Your Card',
        subtitle: 'Premium digital cards for every professional',
        personal: 'Personal',
        business: 'Business',
        enterprise: 'Enterprise',
        selectPlan: 'Select Plan',
        subscribe: 'Subscribe Now',
        perYear: '/year',
        perProfile: 'per profile',
        selectProduct: 'Select Your Card',
        preview: 'Card Preview',
        selectColor: 'Select Color',
        quantity: 'Quantity',
        orderSummary: 'Order Summary',
        cards: 'Cards',
        annualPlan: 'Annual Plan',
        total: 'Total',
        proceedToCheckout: 'Proceed to Checkout',
        createAccount: 'Create Your Account',
        orderCreated: 'Order created successfully!',
        cardFree: 'FREE Card',
        firstYearFreeMessage: '1st Year Subscription FREE',
        renewalNote: 'Renewal (Year 2+)',
        sigleLabel: 'Sigle / Identifier',
        sigleHelp: 'Your unique identifier for BUSINESS URLs (e.g., TECHCORP1000)',
        checkingSigle: 'Checking availability...',
        paymentMode: 'Payment Method',
        stripePayment: 'Automatic Payment (Stripe)',
        stripeDesc: 'Pay securely by card - instant activation',
        recommended: 'Recommended',
        manualPayment: 'Manual Payment',
        manualDesc: 'Pay offline - activation after admin validation',
        manualPaymentNote: 'Note',
        manualPaymentWarning: 'Your order will be activated after we confirm your payment.',
        payNow: 'Pay Now',
        submitOrder: 'Submit Order',
        processing: 'Processing...',
        businessMinQuantity: 'Min. 10 cards',
        orderPending: 'Order submitted successfully!',
        buyFromDashboard: 'Buy from Dashboard',
        getStarted: 'Get Started',
        readyToStart: 'Ready to get started?',
        readyToStartDesc: 'Create your digital business card in minutes.',
        secure: 'Secure Payment',
        instant: 'Instant Activation',
        premium: 'Premium Quality',
        contactInfo: 'Contact Information',
        subtotal: 'Subtotal'
      },
      // Products Catalog (listing page)
      productsCatalog: {
        badge: 'Premium Smart Cards',
        title: 'Our Products',
        subtitle: 'Discover our range of premium connected business cards for every professional.',
        subscription: 'Subscription',
        colors: 'Colors',
        firstYearFree: '1st year FREE',
        moreFeatures: 'more features',
        perCard: 'card',
        free: 'FREE',
        cardIncluded: 'card included',
        viewProduct: 'View Product',
        securePayment: 'Secure Payment',
        instantActivation: 'Instant Activation',
        premiumQuality: 'Premium Quality'
      },
      // Product Detail page
      productDetail: {
        backToProducts: 'Back to Products',
        featuresIncluded: 'Features Included',
        month: 'month',
        year: 'year'
      },
      // Product-specific translations
      productData: {
        brand: {
          name: 'IKATone Brand Card',
          shortDescription: 'Premium connected business card with premium features.',
          description: 'Premium connected business card with premium features.',
          features: [
            'Premium smart card',
            'Annual PRO subscription',
            'Unlimited profile updates',
            'Custom profile',
            'vCard download',
            'Social links integration',
            'Analytics dashboard'
          ]
        },
        custom: {
          name: 'IKATone Custom Design',
          shortDescription: 'Advanced smart card with your own design and premium features.',
          description: 'Advanced smart card with your own design and premium features.',
          features: [
            'Premium smart card',
            'Annual PRO subscription',
            'Unlimited profile updates',
            'Custom profile',
            'vCard download',
            'Social links integration',
            'Analytics dashboard'
          ]
        },
        enterprise: {
          name: 'IKATone Enterprise',
          shortDescription: 'Enterprise solution for teams with advanced customization.',
          description: 'Enterprise solution for teams with advanced customization.',
          features: [
            'Premium smart card',
            'Annual Business subscription',
            'Unlimited profile updates',
            'Custom profile',
            'vCard download',
            'Social links integration',
            'Analytics dashboard',
            'Priority support',
            'Team management',
            'White-label option'
          ]
        },
        haiti_mondial: {
          name: 'IKATone Haiti Mondial',
          shortDescription: 'Smart card supporting the Haitian national team.',
          description: 'Smart card supporting the Haitian national team.',
          features: [
            'Premium smart card',
            'PRO or Business subscription',
            'Unlimited profile updates',
            'Custom profile',
            'vCard download',
            'Social links integration',
            'Analytics dashboard'
          ]
        }
      },
      // Trust badges
      trustBadges: {
        securePayment: 'SSL Secure Payment',
        encryptedInfo: 'Your payment information is encrypted and protected.',
        poweredByStripe: 'Secure payment processing',
        acceptedPayments: 'Accepted payment methods',
        dataProtection: 'Your data is protected with 256-bit SSL encryption. We never store your card details.'
      },
      // Landing page sections
      landing: {
        positioning: 'More than a card. It\'s your digital identity.',
        positioningDesc: 'Join thousands of professionals who have upgraded their networking game.',
        socialProof: {
          title: 'Trusted by professionals',
          review1: 'Game changer for my business. I\'ve never made a stronger first impression.',
          review1Author: 'Marc D., Consultant',
          review2: 'Professional, modern, and incredibly effective. My clients love it.',
          review2Author: 'Sophie L., Real Estate Agent',
          review3: 'Replaced all my paper cards. The analytics are a huge bonus.',
          review3Author: 'David K., Entrepreneur'
        },
        trust: {
          title: 'Your satisfaction, guaranteed',
          securePayment: 'Secure payment',
          securePaymentDesc: 'Protected by Stripe with 256-bit encryption',
          fastDelivery: 'Fast delivery',
          fastDeliveryDesc: 'Get your card within days, worldwide',
          satisfaction: 'Satisfaction guaranteed',
          satisfactionDesc: 'We stand behind every card we create'
        },
        finalCta: {
          title: 'Start making powerful connections today',
          subtitle: 'Join thousands of professionals who trust IKATone.',
          cta: 'Get Your Card'
        },
        faq: {
          title: 'Frequently Asked Questions',
          q1: 'How does the card work?',
          a1: 'Simply bring your IKATone card close to any smartphone. Your digital profile opens instantly in the browser — no app needed for the recipient.',
          q2: 'Can I update my information?',
          a2: 'Yes! You can update your profile anytime from your dashboard. Changes appear instantly when someone taps your card.',
          q3: 'Do I need an app?',
          a3: 'No. Recipients don\'t need any app. Your profile opens directly in their phone\'s browser. You manage your card from our web dashboard.',
          q4: 'How long does delivery take?',
          a4: 'Standard delivery takes 5-10 business days. We ship worldwide with tracking included.',
          q5: 'Is my data secure?',
          a5: 'Absolutely. We use industry-standard encryption and you control exactly what information is visible on your profile.',
          q6: 'Can I use it for my team?',
          a6: 'Yes! Our Enterprise plan supports team management with custom branding and centralized administration.'
        },
        showcase: {
          title: 'Find the perfect card for you',
          subtitle: 'Premium smart cards designed for every professional.',
          viewDetails: 'View Details'
        }
      },
      // Pricing
      pricing: {
        title: 'Simple, Transparent Pricing',
        subtitle: 'Annual subscription. Cancel anytime.',
        annual: 'Annual subscription',
        includes: 'Includes',
        freeShipping: 'Free worldwide shipping',
        lifetimeAccess: 'Profile access while subscribed',
        unlimitedUpdates: 'Unlimited profile updates',
        profileAnalytics: 'Profile view analytics',
        prioritySupport: 'Priority support',
        teamManagement: 'Team management',
        customBranding: 'Custom branding'
      },
      // How It Works
      howItWorks: {
        title: 'How It Works',
        subtitle: 'Get started in three simple steps',
        cta: 'Get Started Today',
        step1: {
          title: 'Choose Your Plan',
          description: 'Select the plan that fits your needs. Annual subscription with flexible options.',
          d1: 'Browse our collection of premium smart cards',
          d2: 'Choose your preferred color and design',
          d3: 'Complete secure checkout',
          d4: 'Free delivery wherever we sell'
        },
        step2: {
          title: 'Create Your Profile',
          description: 'Set up your digital profile with all your contact information and social links.',
          d1: 'Create your account instantly',
          d2: 'Add your photo and bio',
          d3: 'Enter your contact information',
          d4: 'Link your social media profiles'
        },
        step3: {
          title: 'Start Networking',
          description: 'Simply bring your card close to any smartphone to share your profile instantly.',
          d1: 'Bring your card close to any smartphone',
          d2: 'Profile opens instantly — no app needed',
          d3: 'Recipient can save your contact',
          d4: 'Track your profile views in the dashboard'
        },
        contactless: {
          title: 'How does the connected card work?',
          p1: 'Your IKATone card uses contactless technology. Simply bring it close to any smartphone — your digital profile opens instantly in the browser. No app, no scan, no setup required.',
          p2: 'The recipient sees all your information in one tap: phone, email, social media, website. They can save your contact directly. It is the simplest and fastest way to share your professional identity.'
        }
      },
      // Dashboard
      dashboard: {
        title: 'Dashboard',
        welcome: 'Welcome back',
        overview: 'Overview',
        profile: 'Profile',
        card: 'My Card',
        orders: 'Orders',
        settings: 'Settings',
        editProfile: 'Edit Profile',
        viewProfile: 'View Public Profile',
        saveChanges: 'Save Changes',
        noCards: 'No active subscription',
        buyCard: 'Subscribe now',
        cardSerial: 'Profile ID',
        cardActivated: 'Active',
        cardStatus: 'Status',
        subscriptionExpires: 'Subscription expires',
        renewSubscription: 'Renew Subscription'
      },
      // Profile
      profile: {
        bio: 'Bio',
        phone: 'Phone',
        officePhone: 'Office Phone',
        email: 'Email',
        website: 'Website',
        linkedin: 'LinkedIn',
        instagram: 'Instagram',
        whatsapp: 'WhatsApp',
        youtubeChannel: 'YouTube Channel',
        youtubeVideo: 'YouTube Video',
        facebook: 'Facebook',
        discord: 'Discord',
        twitter: 'X (Twitter)',
        tiktok: 'TikTok',
        github: 'GitHub',
        downloadContact: 'Download Contact',
        shareProfile: 'Share Profile',
        copyLink: 'Copy Link',
        linkCopied: 'Link copied!',
        publicProfile: 'Public Profile'
      },
      // Auth
      auth: {
        login: 'Login',
        register: 'Create Account',
        email: 'Email',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        firstName: 'First Name',
        lastName: 'Last Name',
        forgotPassword: 'Forgot password?',
        forgotPasswordDesc: 'Enter your email and we\'ll send you a reset link.',
        sendResetLink: 'Send Reset Link',
        checkYourEmail: 'Check Your Email',
        resetEmailSent: 'Password reset email sent!',
        resetEmailSentDesc: 'We\'ve sent a password reset link to your email address. Check your inbox and follow the instructions.',
        backToLogin: 'Back to Login',
        resetPassword: 'Reset Password',
        newPassword: 'New Password',
        enterNewPassword: 'Enter your new password below.',
        confirmPasswordRequired: 'Please confirm your password',
        passwordsMismatch: 'Passwords do not match',
        passwordResetSuccess: 'Password Reset Successfully!',
        redirectingToLogin: 'Redirecting you to login...',
        invalidResetLink: 'Invalid Reset Link',
        resetLinkExpired: 'This password reset link has expired or is invalid.',
        requestNewLink: 'Request New Link',
        noAccount: "Don't have an account?",
        haveAccount: 'Already have an account?',
        orContinueWith: 'Or continue with',
        google: 'Google',
        invalidEmail: 'Please enter a valid email address',
        invalidPassword: 'Password must be at least 6 characters',
        passwordMismatch: 'Passwords do not match'
      },
      // Contact
      contact: {
        title: 'Get in Touch',
        subtitle: 'Have questions? We\'d love to hear from you.',
        name: 'Name',
        email: 'Email',
        subject: 'Subject',
        message: 'Message',
        send: 'Send Message',
        success: 'Message sent successfully!'
      },
      // FAQ
      faq: {
        title: 'Frequently Asked Questions',
        subtitle: 'Everything you need to know about IKATone',
        q1: 'How does the digital card work?',
        a1: 'Simply tap your IKATone card on any smartphone. The recipient\'s phone will automatically open your digital profile - no app download required.',
        q2: 'What phones are compatible?',
        a2: 'All modern smartphones are compatible. This includes iPhone XS and newer, and most Android phones from the last 5 years.',
        q3: 'Can I update my profile?',
        a3: 'Yes! You can update your digital profile anytime through your dashboard. Changes appear instantly.',
        q4: 'How does the subscription work?',
        a4: 'IKATone uses annual subscriptions. Your profile remains active as long as your subscription is valid.',
        q5: 'What if I lose my card?',
        a5: 'Contact our support team and we\'ll help you transfer your profile to a new card.',
        q6: 'Is my data secure?',
        a6: 'Yes, we use industry-standard encryption to protect your data. You control what information is visible on your public profile.'
      },
      // CTA
      cta: {
        readyToTransform: 'Ready to Transform Your Networking?',
        joinThousands: 'Join thousands of professionals who\'ve upgraded to smart business cards.',
        getStarted: 'Get Started Today'
      },
      // Common
      common: {
        loading: 'Loading...',
        error: 'An error occurred',
        success: 'Success!',
        save: 'Save',
        saving: 'Saving...',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        back: 'Back',
        next: 'Next',
        submit: 'Submit',
        learnMore: 'Learn More',
        refresh: 'Refresh',
        viewAll: 'View all',
        uploading: 'Uploading...',
        dismiss: 'Dismiss',
        backToHome: 'Back to Home',
        search: 'Search...'
      },
      footer: {
        tagline: 'Smart networking for the modern professional.',
        company: 'Company',
        about: 'About Us',
        careers: 'Careers',
        press: 'Press',
        support: 'Support',
        help: 'Help Center',
        terms: 'Terms of Service',
        privacy: 'Privacy Policy',
        legal: 'Legal',
        copyright: '© 2024 IKATone. All rights reserved.'
      },
      // Legal Pages
      legal: {
        lastUpdated: 'Last updated',
        toComplete: '[to be completed]',
        contact: 'Contact',
        privacy: {
          title: 'Privacy Policy',
          section1: {
            title: '1. About IKATone',
            p1: 'IKATone is a digital platform that offers connected physical cards allowing users to create and manage professional digital profiles. These profiles are accessible online and can be easily shared through contactless technology integrated into our cards.',
            p2: 'The protection of your personal data is at the heart of our concerns. This privacy policy describes how we collect, use, store, and protect your personal information, in accordance with the Personal Information Protection and Electronic Documents Act (PIPEDA) of Canada.'
          },
          section2: {
            title: '2. Personal Data Collected',
            intro: 'As part of our services, we collect the following types of data:',
            identification: 'Identification Information',
            idList: ['First and last name', 'Email address', 'Password (stored encrypted)', 'Profile photo (optional)'],
            professional: 'Professional Information',
            proList: ['Job title', 'Company name', 'Professional biography', 'Phone numbers', 'Website', 'Social media links'],
            order: 'Order Information',
            orderList: ['Shipping address', 'Order history', 'Subscription information'],
            technical: 'Technical Data',
            techList: ['IP address', 'Browser type', 'Platform usage data', 'Profile view statistics']
          },
          section3: {
            title: '3. Use of Data',
            intro: 'We use your personal data for the following purposes:',
            list: [
              'Service provision: creation and management of your account, digital profiles, and connected cards.',
              'Order processing: management of purchases, deliveries, and subscriptions.',
              'Communication: sending order confirmations, service notifications, and important information about your account.',
              'Service improvement: analysis of usage data to optimize the user experience.',
              'Customer support: answering your questions and resolving technical issues.',
              'Security: protecting your account and preventing fraud.'
            ]
          },
          section4: {
            title: '4. Public Profiles and User Responsibility',
            p1: 'Profiles created on IKATone are public by default. This means that anyone with the link to your profile or using your connected card can view the information you have published there.',
            p2: 'You are responsible for the information you choose to make public. We recommend that you:',
            list: [
              'Only share professional information that you wish to make accessible.',
              'Regularly check the content of your profile.',
              'Use the available visibility settings to hide your profile if necessary.'
            ],
            p3: 'IKATone cannot be held responsible for the use by third parties of information that you have chosen to make public.'
          },
          section5: {
            title: '5. Data Retention and Deletion',
            retention: 'Retention Period',
            retentionText: 'Your personal data is retained as long as your account is active. If your subscription expires, your data and profiles remain stored but may become inactive depending on your account settings. No automatic deletion is performed when a subscription expires.',
            deletion: 'Data Deletion',
            deletionIntro: 'You can at any time:',
            deletionList: [
              'Delete your profile yourself from your dashboard.',
              'Request complete deletion of your account and all your data by contacting us.'
            ],
            deletionNote: 'Following a deletion request, your data will be erased from our systems within 30 days, except for information we are legally required to retain (invoices, transaction history).'
          },
          section6: {
            title: '6. Sharing Data with Third Parties',
            intro: 'We never sell your personal data. We only share your information with the following third parties, strictly within the scope of our operations:',
            payment: 'Payment Providers',
            paymentText: 'We use Stripe and PayPal to process payments. These providers receive the information necessary to process your transactions. IKATone does not store any banking or credit card data.',
            hosting: 'Hosting Services',
            hostingText: 'Your data is hosted on secure servers provided by our infrastructure partners.',
            legal: 'Legal Obligations',
            legalText: 'We may be required to disclose your data if required by law or to respond to a valid legal request from competent authorities.'
          },
          section7: {
            title: '7. Data Security',
            intro: 'We implement technical and organizational security measures to protect your personal data:',
            list: [
              'Encryption of sensitive data (passwords).',
              'Secure connections (HTTPS).',
              'Restricted access to personal data to authorized personnel only.',
              'Regular monitoring of our systems.',
              'Regular data backup.'
            ],
            note: 'Despite these measures, no system is completely infallible. In the event of a data breach, we are committed to informing you as soon as possible in accordance with our legal obligations.'
          },
          section8: {
            title: '8. User Rights',
            intro: 'In accordance with PIPEDA and applicable legislation, you have the following rights:',
            list: [
              'Right of access: you can request a copy of the personal data we hold about you.',
              'Right of rectification: you can modify your information directly from your dashboard or ask us to correct it.',
              'Right of deletion: you can delete your profile yourself or request the deletion of your account.',
              'Right to withdraw consent: you can withdraw your consent at any time for processing based on it.',
              'Right to complain: you can file a complaint with the Office of the Privacy Commissioner of Canada.'
            ],
            note: 'To exercise these rights, please contact us at the address indicated below.'
          },
          section9: {
            title: '9. Contact',
            intro: 'For any questions regarding this privacy policy or to exercise your rights, you can contact us:',
            department: 'IKATone - Data Protection Service',
            response: 'We are committed to responding to your request within 30 days.'
          }
        },
        terms: {
          title: 'Terms of Service',
          section1: {
            title: '1. Acceptance of Terms',
            p1: 'These terms of use (hereinafter "Terms") govern access to and use of the website, applications, and services offered by IKATone (hereinafter "the Service").',
            p2: 'By accessing or using the Service, you agree to be bound by these Terms. If you do not accept these Terms, you must not use the Service.',
            p3: 'IKATone reserves the right to modify these Terms at any time. Changes will take effect upon publication on the site. Your continued use of the Service after publication of changes constitutes your acceptance of the new Terms.'
          },
          section2: {
            title: '2. Eligibility',
            intro: 'To use the IKATone Service, you must:',
            list: [
              'Be at least 18 years old.',
              'Have the legal capacity to enter into a binding contract.',
              'Not have been previously suspended or banned from the Service.',
              'Provide accurate and complete information when registering.'
            ],
            note: 'By using the Service, you represent and warrant that you meet all these eligibility requirements.'
          },
          section3: {
            title: '3. Description of Service',
            intro: 'IKATone offers a solution of connected cards associated with professional digital profiles. The Service includes:',
            list: [
              'Connected physical cards: cards equipped with contactless technology allowing instant sharing of your digital profile.',
              'Digital profiles: customizable web pages containing your professional information, contact details, and links to your social networks.',
              'Dashboard: management interface to modify your profiles, view statistics, and manage your account.',
              'Subscription: access to Service features through an annual subscription.'
            ]
          },
          section4: {
            title: '4. Payments and Subscriptions',
            pricing: 'Pricing',
            pricingText: 'The IKATone Service is offered in the form of a card purchase combined with an annual subscription. Current rates are displayed on our website at the time of purchase.',
            payment: 'Payment Methods',
            paymentText: 'Payments are processed by our partners Stripe and PayPal. By making a payment, you accept the terms of use of these providers. IKATone does not store any banking or credit card data.',
            renewal: 'Renewal',
            renewalText: 'Subscriptions are not automatically renewed. You will receive a notification before your subscription expires and can choose to renew from your dashboard.',
            refunds: 'Refunds',
            refundsText: 'Physical cards are personalized products. Once the order is confirmed and shipped, no refund will be granted except in case of manufacturing defect. For subscriptions, no partial refund is provided in case of early termination.'
          },
          section5: {
            title: '5. User Obligations',
            intro: 'By using the Service, you agree to:',
            list: [
              'Provide accurate, current, and complete information.',
              'Maintain the confidentiality of your login credentials.',
              'Use the Service legally and ethically.',
              'Not impersonate another person or entity.',
              'Not publish illegal, defamatory, obscene, or offensive content.',
              'Not use the Service for spam or harassment purposes.',
              'Not attempt to compromise the security of the Service.',
              'Respect the intellectual property rights of others.'
            ],
            note: 'You are solely responsible for the content you publish on your profile. IKATone reserves the right to remove any content that violates these Terms.'
          },
          section6: {
            title: '6. Account Management and Deletion',
            management: 'Account Management',
            managementText: 'You can modify your account and profile information at any time from your dashboard. You can also temporarily hide your profiles without deleting them.',
            deletion: 'Account Deletion',
            deletionIntro: 'You can delete your account in two ways:',
            deletionList: [
              'By deleting your profiles directly from your dashboard.',
              'By contacting us to request complete deletion of your account and data.'
            ],
            deletionNote: 'Account deletion is permanent. Once deleted, your account and data cannot be recovered.',
            expiration: 'Subscription Expiration',
            expirationText: 'If your subscription expires, your account and profiles are not automatically deleted. Your profiles may become inactive or invisible, but your data is retained. You can reactivate your account by renewing your subscription.'
          },
          section7: {
            title: '7. Suspension or Termination of Service',
            intro: 'IKATone reserves the right to suspend or terminate your access to the Service, without notice, in the following cases:',
            list: [
              'Violation of these Terms of Use.',
              'Publication of illegal or inappropriate content.',
              'Fraudulent use of the Service.',
              'Non-payment of fees due.',
              'Request from competent authorities.',
              'Any behavior detrimental to IKATone or other users.'
            ],
            note: 'In case of termination for violation of Terms, no refund will be granted.'
          },
          section8: {
            title: '8. Limitation of Liability',
            intro: 'The IKATone Service is provided "as is" and "as available". To the extent permitted by applicable law:',
            list: [
              'IKATone does not guarantee that the Service will be uninterrupted, secure, or error-free.',
              'IKATone disclaims all liability for indirect, incidental, special, or consequential damages arising from the use of the Service.',
              'IKATone is not responsible for content published by users on their profiles.',
              'IKATone is not responsible for data loss resulting from circumstances beyond its control.',
              'IKATone\'s total liability is limited to the amount you have paid for the Service in the last 12 months.'
            ],
            note: 'You use the Service at your own risk and are solely responsible for the accuracy of the information you publish.'
          },
          section9: {
            title: '9. Intellectual Property',
            p1: 'The IKATone Service, including its design, features, source code, texts, images, and logos, is the exclusive property of IKATone and is protected by intellectual property laws.',
            p2: 'You are not authorized to:',
            list: [
              'Copy, modify, or distribute the content of the Service without authorization.',
              'Use IKATone\'s trademarks, logos, or trade names without prior agreement.',
              'Reverse engineer the Service.'
            ],
            p3: 'You retain ownership of the content you publish on your profiles. By using the Service, you grant IKATone a limited license to display this content as part of the operation of the Service.'
          },
          section10: {
            title: '10. Applicable Law',
            p1: 'These Terms are governed by and construed in accordance with the laws of Canada. Any dispute relating to the Service shall be subject to the exclusive jurisdiction of the Canadian courts.',
            p2: 'If any provision of these Terms is found to be invalid or unenforceable, the other provisions shall remain in full force and effect.'
          },
          section11: {
            title: '11. Contact',
            intro: 'For any questions regarding these Terms of Use, you can contact us:',
            department: 'IKATone - Customer Service'
          }
        }
      },
      // Errors
      errors: {
        profileInactive: 'This profile is temporarily inactive. Please renew the subscription.',
        profileNotFound: 'Profile not found',
        unauthorized: 'Please login to continue',
        serverError: 'Something went wrong. Please try again.',
        adminRequired: 'Admin access required',
        orderFailed: 'Failed to create order. Please try again.',
        saveFailed: 'Failed to save. Please try again.',
        operationFailed: 'Operation failed. Please try again.',
        invalidImageType: 'Please upload an image file',
        imageTooLarge: 'Image must be less than 5MB',
        uploadFailed: 'Failed to upload image',
        sigleRequired: 'Business sigle is required',
        sigleNotAvailable: 'This sigle is not available',
        emailRequired: 'Email is required',
        invalidEmail: 'Invalid email address',
        passwordRequired: 'Password is required',
        passwordLength: 'Password must be at least 6 characters',
        firstNameRequired: 'First name is required',
        lastNameRequired: 'Last name is required'
      },
      // Owner Dashboard
      owner: {
        dashboard: 'My Dashboard',
        welcomeBack: 'Welcome back',
        backToSite: 'Back to Site',
        buyMoreCards: 'Buy More Cards',
        businessAccount: 'Business Account',
        sigleValidated: 'Sigle validated - Your profiles are active',
        siglePending: 'Sigle pending validation by admin',
        businessExplanation: 'BUSINESS profiles are designed for companies and teams. All your team members share the same professional identity.',
        profileUrlFormat: 'Profile URL format',
        totalProfiles: 'Total Profiles',
        activeProfiles: 'Active Profiles',
        totalCards: 'Total Cards',
        totalOrders: 'Total Orders',
        totalViews: 'Total Views',
        views: 'views',
        profileAnalytics: 'Profile Analytics',
        avgViewsPerProfile: 'Avg. per Profile',
        topProfiles: 'Top Performing Profiles',
        recentVisitors: 'Recent Visitors',
        viewsToday: 'view(s) today',
        myProfiles: 'My Profiles',
        noProfiles: 'No profiles yet',
        getStarted: 'Get Started',
        noJobTitle: 'No title',
        subscriptionActive: 'Active subscription',
        subscriptionExpired: 'Expired subscription',
        profileVisible: 'Visible',
        profileHidden: 'Hidden',
        renewToActivate: 'Renew subscription to activate this profile',
        renew: 'Renew',
        subscriptionSummary: 'Subscription Summary',
        activeSubscriptions: 'Active Subscriptions',
        expiredSubscriptions: 'Expired Subscriptions',
        renewalCost: 'Renewal cost',
        year: 'year',
        manageSubscriptions: 'Manage Subscriptions',
        recentOrders: 'Recent Orders',
        noOrders: 'No orders yet',
        editProfile: 'Edit Profile',
        viewPublicProfile: 'View Public Profile',
        profilePhoto: 'Profile Photo',
        uploadPhoto: 'Upload Photo',
        photoRequirements: 'JPG, PNG or GIF. Max 5MB.',
        profileBanner: 'Profile Banner',
        selectColor: 'Background Color',
        uploadBannerImage: 'Or upload a custom image',
        uploadImage: 'Choose Image',
        bannerUpdated: 'Banner updated',
        basicInfo: 'Basic Information',
        firstName: 'First Name',
        lastName: 'Last Name',
        jobTitle: 'Job Title',
        company: 'Company',
        bio: 'Bio',
        bioPlaceholder: 'Tell us about yourself...',
        contactInfo: 'Contact Information',
        phone: 'Mobile Phone',
        officePhone: 'Office Phone',
        email: 'Email',
        website: 'Website',
        socialLinks: 'Social Links',
        youtubeChannel: 'YouTube Channel',
        youtubeVideo: 'YouTube Video',
        profileSaved: 'Profile saved successfully',
        photoUploaded: 'Photo uploaded successfully',
        profileActivated: 'Profile activated',
        profileDeactivated: 'Profile deactivated',
        // New translations for features A-G
        currentPlan: 'Current Plan',
        cardsLimit: 'Cards Limit',
        expiresOn: 'Expires on',
        generateEditLink: 'Generate edit link',
        editLinkCopied: 'Edit link copied to clipboard!',
        editLinkExpires: 'Link expires on {{date}}',
        toggleDisabledPending: 'Toggle disabled until payment is validated',
        hideProfile: 'Hide profile',
        showProfile: 'Show profile',
        myCards: 'My Cards',
        noCards: 'No cards yet',
        sortNewest: 'Newest first',
        sortOldest: 'Oldest first',
        sortMostViewed: 'Most viewed',
        sortSigle: 'Sigle (numeric)'
      },
      // Admin Dashboard
      admin: {
        dashboard: 'Admin Dashboard',
        manageEverything: 'Manage users, profiles and orders',
        totalUsers: 'Total Users',
        totalProfiles: 'Total Profiles',
        activeProfiles: 'Active Profiles',
        paidProfiles: 'Paid Profiles',
        pendingSigles: 'Pending Sigles',
        pendingOrders: 'Pending Orders',
        tabOverview: 'Overview',
        tabUsers: 'Users',
        tabProfiles: 'Profiles',
        tabOrders: 'Orders',
        searchPlaceholder: 'Search users, profiles...',
        pendingSigleValidation: 'Pending Sigle Validation',
        pendingPayments: 'Pending Payments',
        validate: 'Validate',
        reject: 'Reject',
        markPaid: 'Mark Paid',
        allCaughtUp: 'All caught up!',
        noPendingActions: 'No pending actions at the moment.',
        allUsers: 'All Users',
        email: 'Email',
        name: 'Name',
        type: 'Type',
        sigle: 'Sigle',
        profiles: 'Profiles',
        created: 'Created',
        allProfiles: 'All Profiles',
        profileId: 'Profile ID',
        owner: 'Owner',
        subscription: 'Subscription',
        status: 'Status',
        actions: 'Actions',
        active: 'Active',
        expired: 'Expired',
        visible: 'Visible',
        hidden: 'Hidden',
        allOrders: 'All Orders',
        orderId: 'Order ID',
        product: 'Product',
        quantity: 'Quantity',
        total: 'Total',
        date: 'Date',
        sigleValidated: 'Sigle validated successfully',
        sigleRejected: 'Sigle rejected',
        orderMarkedPaid: 'Order marked as paid',
        profileActivated: 'Profile activated',
        profileDeactivated: 'Profile deactivated'
      },
      // Products Page (New)
      products: {
        title: 'Choose Your Card',
        subtitle: 'Premium smart cards for every professional',
        cards: 'cards',
        card: 'card',
        cardFree: 'FREE Card',
        freeCard: 'Free card',
        profile: 'profile',
        selectProduct: 'Select Your Card',
        preview: 'Card Preview',
        selectColor: 'Select Color',
        quantity: 'Quantity',
        month: 'month',
        year: 'year',
        selectPlan: 'Select Plan',
        choosePlan: 'Choose Your Plan',
        businessSigle: 'Business Acronym',
        sigleRequired: 'Business acronym is required',
        sigleTooLong: 'Acronym must be 8 characters max',
        sigleInvalid: 'Only letters and numbers allowed',
        sigleLabel: 'Sigle / Identifier',
        sigleHelp: 'Your profiles will be: SIGLE0001, SIGLE0002... (awaiting admin validation)',
        checkingSigle: 'Checking availability...',
        businessMinQuantity: 'BUSINESS plan requires minimum 10 cards',
        includes: 'What\'s Included',
        feature1: 'Premium smart card',
        feature2: 'Annual PRO subscription',
        feature2Free: '1st year subscription FREE',
        feature3: 'Unlimited profile updates',
        feature4: 'Profile view analytics',
        orderSummary: 'Order Summary',
        annualPlan: 'Annual Plan',
        total: 'Total',
        firstYear: 'First year subscription',
        firstYearFree: '1st year FREE',
        firstYearFreeLabel: '1st year subscription FREE',
        firstYearFreeMessage: '1st Year Subscription FREE',
        renewalYear2: 'Renewal (Year 2+)',
        renewalNote: 'Renewal (Year 2+)',
        proceedToCheckout: 'Proceed to Checkout',
        createAccount: 'Create Your Account',
        placeOrder: 'Place Order',
        paymentNote: 'Payment is processed manually. You will be contacted to complete payment.',
        orderCreated: 'Order created successfully!',
        paymentMode: 'Payment Method',
        stripePayment: 'Automatic Payment (Stripe)',
        stripeDesc: 'Pay securely by card - instant activation',
        recommended: 'Recommended',
        manualPayment: 'Manual Payment',
        manualDesc: 'Pay offline - activation after admin validation',
        manualPaymentNote: 'Note',
        manualPaymentWarning: 'Your order will be activated after we confirm your payment.',
        payNow: 'Pay Now',
        submitOrder: 'Submit Order',
        processing: 'Processing...',
        contactInfo: 'Your Information',
        stripeInfoNote: 'Your account will be created automatically after payment. You will receive your login credentials by email.',
        buyFromDashboard: 'Buy from Dashboard',
        getStarted: 'Get Started',
        readyToStart: 'Ready to get started?',
        readyToStartDesc: 'Create your digital business card in minutes.',
        secure: 'Secure Payment',
        instant: 'Instant Activation',
        premium: 'Premium Quality'
      },
      // Subscriptions Page
      subscriptions: {
        title: 'Manage Subscriptions',
        subtitle: 'Renew your profile subscriptions to keep them active',
        activeProfiles: 'Active',
        expiringSoon: 'Expiring Soon',
        expiredProfiles: 'Expired',
        selectProfiles: 'Select profiles to renew',
        selectExpired: 'Select Expired',
        selectAll: 'Select All',
        clearAll: 'Clear',
        noProfiles: 'No profiles to display',
        expired: 'Expired',
        daysLeft: 'days left',
        active: 'Active',
        renewalSummary: 'Renewal Summary',
        profilesSelected: 'Profiles selected',
        pricePerProfile: 'Price per profile',
        year: 'year',
        total: 'Total',
        annualSubscription: 'Annual subscription (12 months)',
        requestRenewal: 'Request Renewal',
        paymentNote: 'You will be contacted to complete your payment.',
        renewalSubmitted: 'Renewal request submitted successfully!',
        pendingPayments: 'Pending Payments',
        awaitingPayment: 'Awaiting Payment',
        profilesLabel: 'profiles',
        profilesConcerned: 'Profiles concerned',
        createdAt: 'Created',
        contactForPayment: 'Please contact us to complete your payment and activate your profiles.',
        renewalRequestSent: 'Renewal Request Sent',
        orderId: 'Order ID',
        awaitingAdminValidation: 'Your request is awaiting admin validation. You will be contacted for payment.',
        subtotal: 'Subtotal'
      },
      // Account Status
      account: {
        pendingTitle: 'Account Pending Validation',
        pendingMessage: 'Your account is awaiting validation. You will receive an email once it is approved.',
        loggedInAs: 'Logged in as',
        pendingOrders: 'Pending Orders',
        whatToExpect: 'What to expect',
        activationEmail: 'You will receive an email when your account is activated and ready to use.',
        checkStatus: 'Check Status'
      },
      // Checkout Pages
      checkout: {
        processing: 'Processing Payment',
        pleaseWait: 'Please wait while we confirm your payment...',
        paymentSuccessful: 'Payment Successful!',
        thankYou: 'Thank you for your purchase. Your IKATone card will be shipped soon.',
        amountPaid: 'Amount Paid',
        downloadInvoice: 'Download Invoice',
        goDashboard: 'Go to Dashboard',
        orderCreated: 'Order Created!',
        orderPendingTitle: 'Order Awaiting Validation',
        orderPendingMessage: 'Your order has been created and is awaiting validation. You will receive an email once it is approved.',
        pendingPaymentMessage: 'Your order has been created. Complete the payment to activate your profiles.',
        orderNumber: 'Order Number',
        totalAmount: 'Total Amount',
        profilesCreated: 'Profiles Created',
        firstYearFreeNote: '1st Year Subscription FREE',
        nextSteps: 'Next Steps',
        nextStepsDetails: 'Complete your payment via Stripe, or contact us for manual payment. Your profiles will be activated once payment is confirmed.',
        whatHappensNext: 'What happens next?',
        pendingStep1: 'Our team will review your order and contact you for payment.',
        pendingStep2: 'Once payment is confirmed, your account will be activated.',
        pendingStep3: 'You will receive an email with your login details and dashboard access.',
        emailConfirmation: 'Confirmation will be sent to',
        pendingNotice: 'Please keep this order number for your records. You will need it when completing your payment.',
        contactUs: 'Contact Us',
        sessionExpired: 'Session Expired',
        sessionExpiredMessage: 'Your checkout session has expired. Please try again.',
        paymentIssue: 'Payment Issue',
        paymentIssueMessage: 'There was an issue processing your payment. Please try again or contact support.',
        tryAgain: 'Try Again',
        contactSupport: 'Contact Support',
        paymentCancelled: 'Payment Cancelled',
        paymentCancelledMessage: 'You cancelled the payment. Your order has been saved.',
        orderSavedMessage: 'You can complete your payment anytime from your dashboard.',
        retryPayment: 'Retry Payment',
        goToDashboardToPay: 'Go to Dashboard to Pay',
        needHelp: 'Need Help?',
        promoCode: 'Promo Code',
        promoPlaceholder: 'Enter your code',
        apply: 'Apply',
        couponApplied: 'Coupon applied successfully',
        couponInvalid: 'Invalid or expired coupon'
      },
      // Settings Pages
      settings: {
        title: 'Settings',
        adminTitle: 'Admin Settings',
        accountInfo: 'Account Information',
        email: 'Email',
        name: 'Name',
        plan: 'Plan',
        role: 'Role',
        changePassword: 'Change Password',
        currentPassword: 'Current Password',
        newPassword: 'New Password',
        confirmPassword: 'Confirm Password',
        updatePassword: 'Update Password',
        passwordChanged: 'Password changed successfully!',
        passwordsNoMatch: 'New passwords do not match'
      },
      // Profile Edit (Secure Link)
      profileEdit: {
        title: 'Edit Your Profile',
        subtitle: 'Update your public profile information',
        invalidLink: 'This link is invalid or has expired',
        linkExpired: 'Link Expired',
        expiresOn: 'Link expires on',
        profileId: 'Profile ID',
        saveChanges: 'Save Changes',
        saved: 'Profile updated successfully!',
        success: 'Profile Updated!',
        successMessage: 'Your changes have been saved. You can now close this page.',
        viewProfile: 'View My Profile'
      },
      // Buy More Cards
      buyCards: {
        title: 'Buy More Cards',
        subtitle: 'Add new cards to your existing subscription',
        limitReached: 'Card Limit Reached',
        currentPlan: 'Current Plan',
        cardsUsed: 'cards used',
        backToDashboard: 'Back to Dashboard',
        expiresOn: 'Subscription expires',
        selectCard: 'Select Card Type',
        subscriptionIncluded: '1st year subscription included',
        proratedInfo: 'Prorated Pricing',
        cards: 'Cards',
        subscription: 'Subscription',
        months: 'months',
        subscriptionFree: '1st year subscription FREE'
      }
    }
  },
  fr: {
    translation: {
      // Navigation
      nav: {
        home: 'Accueil',
        products: 'Produits',
        pricing: 'Tarifs',
        features: 'Fonctionnalités',
        howItWorks: 'Comment ça marche',
        faq: 'FAQ',
        contact: 'Contact',
        login: 'Connexion',
        register: 'Commencer',
        dashboard: 'Tableau de bord',
        logout: 'Déconnexion'
      },
      // Hero
      hero: {
        title: 'Transformez votre première impression en une expérience digitale puissante',
        subtitle: 'Partagez vos informations, vos liens et votre image instantanément en un seul geste. Sans application. Sans papier. Juste de l\'impact.',
        cta: 'Obtenir ma carte',
        ctaSecondary: 'Voir comment ça marche'
      },
      // Value Proposition
      value: {
        title: 'Pourquoi les professionnels choisissent IKATone',
        items: [
          'Impressionnez vos clients instantanément',
          'Partagez tout en un seul geste',
          'Ne manquez jamais de cartes',
          'Mettez à jour vos informations à tout moment',
          'Suivez vos interactions'
        ]
      },
      // Features
      features: {
        title: 'Pourquoi choisir IKATone ?',
        subtitle: 'La technologie premium rencontre le design élégant',
        instant: {
          title: 'Partage Instantané',
          description: 'Partagez vos coordonnées en un seul tap. Aucune app requise.'
        },
        customizable: {
          title: 'Entièrement Personnalisable',
          description: 'Créez votre profil digital parfait avec photos, liens et réseaux sociaux.'
        },
        analytics: {
          title: 'Suivi des Connexions',
          description: 'Voyez combien de fois votre profil a été consulté et partagé.'
        },
        eco: {
          title: 'Écologique',
          description: 'Une carte remplace des milliers de cartes papier. Meilleur pour la planète.'
        },
        secure: {
          title: 'Sécurisé & Privé',
          description: 'Contrôlez ce que vous partagez. Mettez à jour ou masquez vos infos à tout moment.'
        },
        universal: {
          title: 'Compatibilité Universelle',
          description: 'Fonctionne avec tous les smartphones modernes. iOS et Android supportés.'
        },
        realtime: {
          title: 'Mises à jour en temps réel',
          description: 'Modifiez vos informations à tout moment. Votre carte affiche toujours la dernière version.'
        },
        team: {
          title: 'Gestion d\'équipe',
          description: 'Gérez plusieurs cartes et profils depuis un seul tableau de bord.'
        },
        multilang: {
          title: 'Multilingue',
          description: 'Interface disponible en français et en anglais. Touchez un public plus large.'
        }
      },
      // Features Page (redesigned)
      featuresPage: {
        hero: {
          title: 'Tout ce dont vous avez besoin pour marquer les esprits',
          subtitle: 'Partagez vos informations, vos liens et votre image instantanément avec une expérience moderne conçue pour les professionnels.',
          cta: 'Découvrir les fonctionnalités'
        },
        core: {
          title: 'Fonctionnalités principales',
          items: [
            { title: 'Partage instantané', desc: 'Partagez vos informations en un seul geste' },
            { title: 'Profil personnalisé', desc: 'Créez un profil professionnel à votre image' },
            { title: 'Mises à jour illimitées', desc: 'Modifiez vos informations à tout moment' },
            { title: 'Liens sociaux', desc: 'Ajoutez tous vos réseaux et liens' },
            { title: 'Tableau de bord', desc: 'Gérez tout depuis votre espace personnel' },
            { title: 'Statistiques', desc: 'Suivez vos interactions' }
          ]
        },
        benefits: {
          title: 'Pourquoi c\'est important',
          items: [
            'Faites une forte première impression',
            'Démarquez-vous de la concurrence',
            'Simplifiez vos échanges professionnels',
            'Gagnez du temps et de l\'argent',
            'Restez toujours à jour'
          ]
        },
        useCases: {
          title: 'Parfait pour',
          items: ['Entrepreneurs', 'Commerciaux', 'Chefs d\'entreprise', 'Équipes et entreprises', 'Créateurs et influenceurs']
        },
        howItWorks: {
          title: 'Comment ça marche',
          steps: ['Obtenez votre carte', 'Créez votre profil', 'Partagez instantanément', 'Développez votre réseau']
        },
        differentiation: 'Plus qu\'une carte. C\'est votre identité digitale.',
        socialProof: {
          title: 'Ce que les gens disent',
          reviews: ['Simple, puissant et professionnel', 'Parfait pour le networking moderne']
        },
        finalCta: {
          title: 'Commencez à créer des connexions plus fortes dès aujourd\'hui',
          cta: 'Obtenir ma carte'
        }
      },
      // Products
      products: {
        title: 'Choisissez Votre Carte',
        subtitle: 'Cartes digitales premium pour chaque professionnel',
        personal: 'Personnel',
        business: 'Business',
        enterprise: 'Entreprise',
        selectPlan: 'Choisir ce plan',
        subscribe: 'S\'abonner',
        perYear: '/an',
        perProfile: 'par profil',
        selectProduct: 'Sélectionnez Votre Carte',
        preview: 'Aperçu de la Carte',
        selectColor: 'Sélectionnez la Couleur',
        quantity: 'Quantité',
        orderSummary: 'Résumé de la Commande',
        cards: 'Cartes',
        annualPlan: 'Plan Annuel',
        total: 'Total',
        proceedToCheckout: 'Passer à la Commande',
        createAccount: 'Créez Votre Compte',
        orderCreated: 'Commande créée avec succès !',
        cardFree: 'Carte GRATUITE',
        firstYearFreeMessage: '1ère Année d\'Abonnement GRATUITE',
        renewalNote: 'Renouvellement (Année 2+)',
        sigleLabel: 'Sigle / Identifiant',
        sigleHelp: 'Vos profils seront : SIGLE1000, SIGLE1001... (en attente de validation admin)',
        checkingSigle: 'Vérification en cours...',
        paymentMode: 'Mode de Paiement',
        stripePayment: 'Paiement Automatique (Stripe)',
        stripeDesc: 'Payez par carte sécurisée - activation immédiate',
        recommended: 'Recommandé',
        manualPayment: 'Paiement Manuel',
        manualDesc: 'Paiement hors ligne - activation après validation admin',
        manualPaymentNote: 'Note',
        manualPaymentWarning: 'Votre commande sera activée après confirmation de votre paiement.',
        payNow: 'Payer Maintenant',
        submitOrder: 'Soumettre la Commande',
        processing: 'Traitement...',
        businessMinQuantity: 'Min. 10 cartes',
        orderPending: 'Commande soumise avec succès !',
        buyFromDashboard: 'Acheter depuis le tableau de bord',
        getStarted: 'Commencer',
        readyToStart: 'Prêt à commencer ?',
        readyToStartDesc: 'Créez votre carte de visite digitale en quelques minutes.',
        secure: 'Paiement sécurisé',
        instant: 'Activation instantanée',
        premium: 'Qualité premium',
        contactInfo: 'Informations de contact',
        subtotal: 'Sous-total'
      },
      // Checkout
      checkout: {
        badge: 'Cartes Connectées Premium',
        title: 'Nos Produits',
        subtitle: 'Découvrez notre gamme de cartes de visite connectées premium pour chaque professionnel.',
        subscription: 'Abonnement',
        colors: 'Couleurs',
        firstYearFree: '1ère année GRATUITE',
        moreFeatures: 'fonctionnalités de plus',
        perCard: 'carte',
        free: 'GRATUIT',
        cardIncluded: 'carte incluse',
        viewProduct: 'Voir le Produit',
        securePayment: 'Paiement sécurisé',
        instantActivation: 'Activation instantanée',
        premiumQuality: 'Qualité premium'
      },
      // Product Detail page
      productDetail: {
        backToProducts: 'Retour aux Produits',
        featuresIncluded: 'Fonctionnalités incluses',
        month: 'mois',
        year: 'an'
      },
      // Product-specific translations
      productData: {
        brand: {
          name: 'IKATone Brand Card',
          shortDescription: 'Carte de visite connectée premium avec fonctionnalités avancées.',
          description: 'Carte de visite connectée premium avec fonctionnalités avancées.',
          features: [
            'Carte connectée premium',
            'Abonnement annuel PRO',
            'Mises à jour de profil illimitées',
            'Profil personnalisé',
            'Téléchargement vCard',
            'Intégration des réseaux sociaux',
            'Tableau de bord analytique'
          ]
        },
        custom: {
          name: 'IKATone Custom Design',
          shortDescription: 'Carte connectée avancée avec votre propre design et fonctionnalités premium.',
          description: 'Carte connectée avancée avec votre propre design et fonctionnalités premium.',
          features: [
            'Carte connectée premium',
            'Abonnement annuel PRO',
            'Mises à jour de profil illimitées',
            'Profil personnalisé',
            'Téléchargement vCard',
            'Intégration des réseaux sociaux',
            'Tableau de bord analytique'
          ]
        },
        enterprise: {
          name: 'IKATone Entreprise',
          shortDescription: 'Solution entreprise pour les équipes avec personnalisation avancée.',
          description: 'Solution entreprise pour les équipes avec personnalisation avancée.',
          features: [
            'Carte connectée premium',
            'Abonnement annuel Business',
            'Mises à jour de profil illimitées',
            'Profil personnalisé',
            'Téléchargement vCard',
            'Intégration des réseaux sociaux',
            'Tableau de bord analytique',
            'Support prioritaire',
            'Gestion d\'équipe',
            'Option marque blanche'
          ]
        },
        haiti_mondial: {
          name: 'IKATone Haïti Mondial',
          shortDescription: 'Carte connectée aux couleurs de l\'équipe nationale haïtienne.',
          description: 'Carte connectée aux couleurs de l\'équipe nationale haïtienne.',
          features: [
            'Carte connectée premium',
            'Abonnement PRO ou Business',
            'Mises à jour de profil illimitées',
            'Profil personnalisé',
            'Téléchargement vCard',
            'Intégration des réseaux sociaux',
            'Tableau de bord analytique'
          ]
        }
      },
      // Trust badges
      trustBadges: {
        securePayment: 'Paiement SSL sécurisé',
        encryptedInfo: 'Vos informations de paiement sont chiffrées et protégées.',
        poweredByStripe: 'Traitement de paiement sécurisé',
        acceptedPayments: 'Moyens de paiement acceptés',
        dataProtection: 'Vos données sont protégées par un chiffrement SSL 256 bits. Nous ne conservons jamais vos informations bancaires.'
      },
      // Landing page sections
      landing: {
        positioning: 'Plus qu\'une carte. C\'est votre identité digitale.',
        positioningDesc: 'Rejoignez des milliers de professionnels qui ont transformé leur façon de réseauter.',
        socialProof: {
          title: 'Ils font confiance à IKATone',
          review1: 'Un outil révolutionnaire pour mon activité. Je n\'ai jamais fait une aussi forte première impression.',
          review1Author: 'Marc D., Consultant',
          review2: 'Professionnel, moderne et très efficace. Mes clients adorent.',
          review2Author: 'Sophie L., Agent immobilier',
          review3: 'J\'ai remplacé toutes mes cartes papier. Les statistiques sont un énorme bonus.',
          review3Author: 'David K., Entrepreneur'
        },
        trust: {
          title: 'Votre satisfaction, garantie',
          securePayment: 'Paiement sécurisé',
          securePaymentDesc: 'Protégé par Stripe avec chiffrement 256 bits',
          fastDelivery: 'Livraison rapide',
          fastDeliveryDesc: 'Recevez votre carte en quelques jours, partout dans le monde',
          satisfaction: 'Satisfaction garantie',
          satisfactionDesc: 'Nous garantissons chaque carte que nous créons'
        },
        finalCta: {
          title: 'Commencez à créer des connexions puissantes dès aujourd\'hui',
          subtitle: 'Rejoignez des milliers de professionnels qui font confiance à IKATone.',
          cta: 'Obtenir ma carte'
        },
        faq: {
          title: 'Questions Fréquentes',
          q1: 'Comment fonctionne la carte ?',
          a1: 'Approchez simplement votre carte IKATone de n\'importe quel smartphone. Votre profil digital s\'ouvre instantanément dans le navigateur — aucune application requise.',
          q2: 'Puis-je modifier mes informations ?',
          a2: 'Oui ! Vous pouvez mettre à jour votre profil à tout moment depuis votre tableau de bord. Les changements apparaissent instantanément.',
          q3: 'Ai-je besoin d\'une application ?',
          a3: 'Non. Les destinataires n\'ont besoin d\'aucune application. Votre profil s\'ouvre directement dans le navigateur de leur téléphone. Vous gérez votre carte depuis notre interface web.',
          q4: 'Quel est le délai de livraison ?',
          a4: 'La livraison standard prend 5 à 10 jours ouvrables. Nous livrons dans le monde entier avec suivi inclus.',
          q5: 'Mes données sont-elles sécurisées ?',
          a5: 'Absolument. Nous utilisons un chiffrement aux standards de l\'industrie et vous contrôlez exactement quelles informations sont visibles sur votre profil.',
          q6: 'Puis-je l\'utiliser pour mon équipe ?',
          a6: 'Oui ! Notre offre Entreprise prend en charge la gestion d\'équipe avec une personnalisation de marque et une administration centralisée.'
        },
        showcase: {
          title: 'Trouvez la carte parfaite pour vous',
          subtitle: 'Des cartes connectées premium conçues pour chaque professionnel.',
          viewDetails: 'Voir les détails'
        }
      },
      // Pricing
      pricing: {
        title: 'Tarifs Simples et Transparents',
        subtitle: 'Abonnement annuel. Annulez à tout moment.',
        annual: 'Abonnement annuel',
        includes: 'Inclus',
        freeShipping: 'Livraison mondiale gratuite',
        lifetimeAccess: 'Accès profil pendant l\'abonnement',
        unlimitedUpdates: 'Mises à jour illimitées',
        profileAnalytics: 'Statistiques de profil',
        prioritySupport: 'Support prioritaire',
        teamManagement: 'Gestion d\'équipe',
        customBranding: 'Marque personnalisée'
      },
      // How It Works
      howItWorks: {
        title: 'Comment ça marche',
        subtitle: 'Commencez en trois étapes simples',
        cta: 'Commencer maintenant',
        step1: {
          title: 'Choisissez Votre Plan',
          description: 'Sélectionnez le plan adapté à vos besoins. Abonnement annuel avec options flexibles.',
          d1: 'Découvrez notre gamme de cartes intelligentes',
          d2: 'Choisissez votre couleur et design préférés',
          d3: 'Finalisez votre commande en toute sécurité',
          d4: 'Livraison gratuite partout ou nous vendons'
        },
        step2: {
          title: 'Créez Votre Profil',
          description: 'Configurez votre profil digital avec toutes vos coordonnées et liens sociaux.',
          d1: 'Créez votre compte en un instant',
          d2: 'Ajoutez votre photo et biographie',
          d3: 'Entrez vos coordonnées professionnelles',
          d4: 'Connectez vos réseaux sociaux'
        },
        step3: {
          title: 'Commencez à Réseauter',
          description: 'Approchez simplement votre carte de n\'importe quel smartphone pour partager votre profil instantanément.',
          d1: 'Approchez votre carte de n\'importe quel téléphone',
          d2: 'Le profil s\'ouvre instantanément — aucune application requise',
          d3: 'Le destinataire peut sauvegarder votre contact',
          d4: 'Suivez les vues de votre profil dans le tableau de bord'
        },
        contactless: {
          title: 'Comment fonctionne la carte connectée ?',
          p1: 'Votre carte IKATone utilise la technologie sans contact. Il suffit de l\'approcher de n\'importe quel smartphone — votre profil digital s\'ouvre instantanément dans le navigateur. Aucune application, aucun scan, aucune configuration.',
          p2: 'Le destinataire voit toutes vos informations en un geste : téléphone, email, réseaux sociaux, site web. Il peut enregistrer votre contact directement. C\'est la manière la plus simple et rapide de partager votre identité professionnelle.'
        }
      },
      // Dashboard
      dashboard: {
        title: 'Tableau de Bord',
        welcome: 'Bon retour',
        overview: 'Aperçu',
        profile: 'Profil',
        card: 'Ma Carte',
        orders: 'Commandes',
        settings: 'Paramètres',
        editProfile: 'Modifier le Profil',
        viewProfile: 'Voir le Profil Public',
        saveChanges: 'Enregistrer',
        noCards: 'Pas d\'abonnement actif',
        buyCard: 'S\'abonner maintenant',
        cardSerial: 'ID Profil',
        cardActivated: 'Actif',
        cardStatus: 'Statut',
        subscriptionExpires: 'Abonnement expire le',
        renewSubscription: 'Renouveler l\'abonnement'
      },
      // Profile
      profile: {
        bio: 'Bio',
        phone: 'Téléphone',
        officePhone: 'Téléphone Bureau',
        email: 'Email',
        website: 'Site Web',
        linkedin: 'LinkedIn',
        instagram: 'Instagram',
        whatsapp: 'WhatsApp',
        youtubeChannel: 'Chaîne YouTube',
        youtubeVideo: 'Vidéo YouTube',
        facebook: 'Facebook',
        discord: 'Discord',
        twitter: 'X (Twitter)',
        tiktok: 'TikTok',
        github: 'GitHub',
        downloadContact: 'Télécharger le Contact',
        shareProfile: 'Partager le Profil',
        copyLink: 'Copier le lien',
        linkCopied: 'Lien copié !',
        publicProfile: 'Profil Public'
      },
      // Auth
      auth: {
        login: 'Connexion',
        register: 'Créer un Compte',
        email: 'Email',
        password: 'Mot de passe',
        confirmPassword: 'Confirmer le mot de passe',
        firstName: 'Prénom',
        lastName: 'Nom',
        forgotPassword: 'Mot de passe oublié ?',
        forgotPasswordDesc: 'Entrez votre email et nous vous enverrons un lien de réinitialisation.',
        sendResetLink: 'Envoyer le lien',
        checkYourEmail: 'Vérifiez vos emails',
        resetEmailSent: 'Email de réinitialisation envoyé !',
        resetEmailSentDesc: 'Nous avons envoyé un lien de réinitialisation à votre adresse email. Vérifiez votre boîte de réception et suivez les instructions.',
        backToLogin: 'Retour à la connexion',
        resetPassword: 'Réinitialiser le mot de passe',
        newPassword: 'Nouveau mot de passe',
        enterNewPassword: 'Entrez votre nouveau mot de passe ci-dessous.',
        confirmPasswordRequired: 'Veuillez confirmer votre mot de passe',
        passwordsMismatch: 'Les mots de passe ne correspondent pas',
        passwordResetSuccess: 'Mot de passe réinitialisé !',
        redirectingToLogin: 'Redirection vers la connexion...',
        invalidResetLink: 'Lien invalide',
        resetLinkExpired: 'Ce lien de réinitialisation a expiré ou est invalide.',
        requestNewLink: 'Demander un nouveau lien',
        noAccount: 'Pas encore de compte ?',
        haveAccount: 'Déjà un compte ?',
        orContinueWith: 'Ou continuer avec',
        google: 'Google',
        invalidEmail: 'Veuillez entrer une adresse email valide',
        invalidPassword: 'Le mot de passe doit contenir au moins 6 caractères',
        passwordMismatch: 'Les mots de passe ne correspondent pas'
      },
      // Contact
      contact: {
        title: 'Contactez-nous',
        subtitle: 'Des questions ? Nous serions ravis de vous entendre.',
        name: 'Nom',
        email: 'Email',
        subject: 'Sujet',
        message: 'Message',
        send: 'Envoyer',
        success: 'Message envoyé avec succès !'
      },
      // FAQ
      faq: {
        title: 'Questions Fréquentes',
        subtitle: 'Tout ce que vous devez savoir sur IKATone',
        q1: 'Comment fonctionne la carte connectée ?',
        a1: 'Approchez simplement votre carte IKATone de n\'importe quel smartphone. Le téléphone du destinataire ouvrira automatiquement votre profil digital — aucune application requise.',
        q2: 'Quels téléphones sont compatibles ?',
        a2: 'Tous les smartphones modernes sont compatibles. Cela inclut iPhone XS et plus récent, et la plupart des téléphones Android des 5 dernières années.',
        q3: 'Puis-je mettre à jour mon profil ?',
        a3: 'Oui ! Vous pouvez mettre à jour votre profil digital à tout moment via votre tableau de bord. Les changements apparaissent instantanément.',
        q4: 'Comment fonctionne l\'abonnement ?',
        a4: 'IKATone utilise des abonnements annuels. Votre profil reste actif tant que votre abonnement est valide.',
        q5: 'Et si je perds ma carte ?',
        a5: 'Contactez notre équipe support et nous vous aiderons à transférer votre profil vers une nouvelle carte.',
        q6: 'Mes données sont-elles sécurisées ?',
        a6: 'Oui, nous utilisons un cryptage standard de l\'industrie pour protéger vos données. Vous contrôlez quelles informations sont visibles sur votre profil public.'
      },
      // CTA
      cta: {
        readyToTransform: 'Prêt à Transformer Votre Networking ?',
        joinThousands: 'Rejoignez des milliers de professionnels qui sont passés aux cartes de visite intelligentes.',
        getStarted: 'Commencer Aujourd\'hui'
      },
      // Common
      common: {
        loading: 'Chargement...',
        error: 'Une erreur est survenue',
        success: 'Succès !',
        save: 'Enregistrer',
        saving: 'Enregistrement...',
        cancel: 'Annuler',
        delete: 'Supprimer',
        edit: 'Modifier',
        back: 'Retour',
        next: 'Suivant',
        submit: 'Soumettre',
        learnMore: 'En savoir plus',
        refresh: 'Actualiser',
        viewAll: 'Voir tout',
        uploading: 'Téléchargement...',
        dismiss: 'Fermer',
        backToHome: 'Retour à l\'Accueil',
        search: 'Rechercher...'
      },
      // Footer
      footer: {
        tagline: 'Networking intelligent pour le professionnel moderne.',
        company: 'Entreprise',
        about: 'À Propos',
        careers: 'Carrières',
        press: 'Presse',
        support: 'Support',
        help: 'Centre d\'Aide',
        terms: 'Conditions d\'Utilisation',
        privacy: 'Politique de Confidentialité',
        legal: 'Mentions Légales',
        copyright: '© 2024 IKATone. Tous droits réservés.'
      },
      // Legal Pages
      legal: {
        lastUpdated: 'Dernière mise à jour',
        toComplete: '[à compléter]',
        contact: 'Contact',
        privacy: {
          title: 'Politique de Confidentialité',
          section1: {
            title: '1. Présentation d\'IKATone',
            p1: 'IKATone est une plateforme numérique qui propose des cartes connectées physiques permettant de créer et de gérer des profils digitaux professionnels. Ces profils sont accessibles en ligne et peuvent être partagés facilement grâce à la technologie sans contact intégrée à nos cartes.',
            p2: 'La protection de vos données personnelles est au cœur de nos préoccupations. La présente politique de confidentialité décrit comment nous collectons, utilisons, conservons et protégeons vos informations personnelles, conformément à la Loi sur la protection des renseignements personnels et les documents électroniques (LPRPDE) du Canada.'
          },
          section2: {
            title: '2. Données personnelles collectées',
            intro: 'Dans le cadre de nos services, nous collectons les types de données suivants :',
            identification: 'Informations d\'identification',
            idList: ['Nom et prénom', 'Adresse courriel', 'Mot de passe (stocké de manière chiffrée)', 'Photo de profil (facultatif)'],
            professional: 'Informations professionnelles',
            proList: ['Titre de poste', 'Nom de l\'entreprise', 'Biographie professionnelle', 'Numéros de téléphone', 'Site web', 'Liens vers les réseaux sociaux'],
            order: 'Informations de commande',
            orderList: ['Adresse de livraison', 'Historique des commandes', 'Informations d\'abonnement'],
            technical: 'Données techniques',
            techList: ['Adresse IP', 'Type de navigateur', 'Données d\'utilisation de la plateforme', 'Statistiques de consultation des profils']
          },
          section3: {
            title: '3. Utilisation des données',
            intro: 'Nous utilisons vos données personnelles aux fins suivantes :',
            list: [
              'Fourniture du service : création et gestion de votre compte, de vos profils digitaux et de vos cartes connectées.',
              'Traitement des commandes : gestion des achats, livraisons et abonnements.',
              'Communication : envoi de confirmations de commande, notifications de service et informations importantes concernant votre compte.',
              'Amélioration du service : analyse des données d\'utilisation pour optimiser l\'expérience utilisateur.',
              'Support client : réponse à vos questions et résolution de problèmes techniques.',
              'Sécurité : protection de votre compte et prévention des fraudes.'
            ]
          },
          section4: {
            title: '4. Profils publics et responsabilité de l\'utilisateur',
            p1: 'Les profils créés sur IKATone sont publics par défaut. Cela signifie que toute personne disposant du lien vers votre profil ou utilisant votre carte connectée peut consulter les informations que vous y avez publiées.',
            p2: 'Vous êtes responsable des informations que vous choisissez de rendre publiques. Nous vous recommandons de :',
            list: [
              'Ne partager que les informations professionnelles que vous souhaitez rendre accessibles.',
              'Vérifier régulièrement le contenu de votre profil.',
              'Utiliser les paramètres de visibilité disponibles pour masquer votre profil si nécessaire.'
            ],
            p3: 'IKATone ne peut être tenu responsable de l\'utilisation par des tiers des informations que vous avez choisi de rendre publiques.'
          },
          section5: {
            title: '5. Conservation et suppression des données',
            retention: 'Durée de conservation',
            retentionText: 'Vos données personnelles sont conservées aussi longtemps que votre compte est actif. En cas d\'expiration de votre abonnement, vos données et profils restent conservés mais peuvent devenir inactifs selon les paramètres de votre compte. Aucune suppression automatique n\'est effectuée en cas d\'abonnement expiré.',
            deletion: 'Suppression des données',
            deletionIntro: 'Vous pouvez à tout moment :',
            deletionList: [
              'Supprimer votre profil vous-même depuis votre tableau de bord.',
              'Demander la suppression complète de votre compte et de toutes vos données en nous contactant.'
            ],
            deletionNote: 'Suite à une demande de suppression, vos données seront effacées de nos systèmes dans un délai de 30 jours, à l\'exception des informations que nous sommes légalement tenus de conserver (factures, historique des transactions).'
          },
          section6: {
            title: '6. Partage des données avec des tiers',
            intro: 'Nous ne vendons jamais vos données personnelles. Nous partageons vos informations uniquement avec les tiers suivants, dans le cadre strict de nos opérations :',
            payment: 'Prestataires de paiement',
            paymentText: 'Nous utilisons Stripe et PayPal pour traiter les paiements. Ces prestataires reçoivent les informations nécessaires au traitement de vos transactions. IKATone ne stocke aucune donnée bancaire ou de carte de crédit.',
            hosting: 'Services d\'hébergement',
            hostingText: 'Vos données sont hébergées sur des serveurs sécurisés fournis par nos partenaires d\'infrastructure.',
            legal: 'Obligations légales',
            legalText: 'Nous pouvons être amenés à divulguer vos données si la loi l\'exige ou pour répondre à une demande légale valide des autorités compétentes.'
          },
          section7: {
            title: '7. Sécurité des données',
            intro: 'Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles pour protéger vos données personnelles :',
            list: [
              'Chiffrement des données sensibles (mots de passe).',
              'Connexions sécurisées (HTTPS).',
              'Accès restreint aux données personnelles au personnel autorisé uniquement.',
              'Surveillance régulière de nos systèmes.',
              'Sauvegarde régulière des données.'
            ],
            note: 'Malgré ces mesures, aucun système n\'est totalement infaillible. En cas de violation de données, nous nous engageons à vous informer dans les meilleurs délais conformément à nos obligations légales.'
          },
          section8: {
            title: '8. Droits des utilisateurs',
            intro: 'Conformément à la LPRPDE et aux législations applicables, vous disposez des droits suivants :',
            list: [
              'Droit d\'accès : vous pouvez demander une copie des données personnelles que nous détenons à votre sujet.',
              'Droit de rectification : vous pouvez modifier vos informations directement depuis votre tableau de bord ou nous demander de les corriger.',
              'Droit de suppression : vous pouvez supprimer votre profil vous-même ou demander la suppression de votre compte.',
              'Droit de retrait du consentement : vous pouvez retirer votre consentement à tout moment pour les traitements basés sur celui-ci.',
              'Droit de porter plainte : vous pouvez déposer une plainte auprès du Commissariat à la protection de la vie privée du Canada.'
            ],
            note: 'Pour exercer ces droits, veuillez nous contacter à l\'adresse indiquée ci-dessous.'
          },
          section9: {
            title: '9. Contact',
            intro: 'Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits, vous pouvez nous contacter :',
            department: 'IKATone - Service Protection des Données',
            response: 'Nous nous engageons à répondre à votre demande dans un délai de 30 jours.'
          }
        },
        terms: {
          title: 'Conditions d\'Utilisation',
          section1: {
            title: '1. Acceptation des conditions',
            p1: 'Les présentes conditions d\'utilisation (ci-après « Conditions ») régissent l\'accès et l\'utilisation du site web, des applications et des services proposés par IKATone (ci-après « le Service »).',
            p2: 'En accédant au Service ou en l\'utilisant, vous acceptez d\'être lié par ces Conditions. Si vous n\'acceptez pas ces Conditions, vous ne devez pas utiliser le Service.',
            p3: 'IKATone se réserve le droit de modifier ces Conditions à tout moment. Les modifications entreront en vigueur dès leur publication sur le site. Votre utilisation continue du Service après la publication des modifications constitue votre acceptation des nouvelles Conditions.'
          },
          section2: {
            title: '2. Éligibilité',
            intro: 'Pour utiliser le Service IKATone, vous devez :',
            list: [
              'Être âgé d\'au moins 18 ans.',
              'Avoir la capacité juridique de conclure un contrat contraignant.',
              'Ne pas avoir été précédemment suspendu ou exclu du Service.',
              'Fournir des informations exactes et complètes lors de votre inscription.'
            ],
            note: 'En utilisant le Service, vous déclarez et garantissez que vous remplissez toutes ces conditions d\'éligibilité.'
          },
          section3: {
            title: '3. Description du service',
            intro: 'IKATone propose une solution de cartes connectées associées à des profils digitaux professionnels. Le Service comprend :',
            list: [
              'Cartes connectées physiques : cartes équipées de technologie sans contact permettant de partager instantanément votre profil digital.',
              'Profils digitaux : pages web personnalisables contenant vos informations professionnelles, coordonnées et liens vers vos réseaux sociaux.',
              'Tableau de bord : interface de gestion permettant de modifier vos profils, consulter les statistiques et gérer votre compte.',
              'Abonnement : accès aux fonctionnalités du Service moyennant un abonnement annuel.'
            ]
          },
          section4: {
            title: '4. Paiements et abonnements',
            pricing: 'Tarification',
            pricingText: 'Le Service IKATone est proposé sous forme d\'achat de carte associé à un abonnement annuel. Les tarifs en vigueur sont affichés sur notre site web au moment de l\'achat.',
            payment: 'Moyens de paiement',
            paymentText: 'Les paiements sont traités par nos partenaires Stripe et PayPal. En effectuant un paiement, vous acceptez les conditions d\'utilisation de ces prestataires. IKATone ne stocke aucune donnée bancaire ou de carte de crédit.',
            renewal: 'Renouvellement',
            renewalText: 'Les abonnements ne sont pas renouvelés automatiquement. Vous recevrez une notification avant l\'expiration de votre abonnement et pourrez choisir de le renouveler depuis votre tableau de bord.',
            refunds: 'Remboursements',
            refundsText: 'Les cartes physiques sont des produits personnalisés. Une fois la commande confirmée et expédiée, aucun remboursement ne sera accordé sauf en cas de défaut de fabrication. Pour les abonnements, aucun remboursement partiel n\'est prévu en cas de résiliation anticipée.'
          },
          section5: {
            title: '5. Obligations de l\'utilisateur',
            intro: 'En utilisant le Service, vous vous engagez à :',
            list: [
              'Fournir des informations exactes, à jour et complètes.',
              'Maintenir la confidentialité de vos identifiants de connexion.',
              'Utiliser le Service de manière légale et éthique.',
              'Ne pas usurper l\'identité d\'une autre personne ou entité.',
              'Ne pas publier de contenu illégal, diffamatoire, obscène ou offensant.',
              'Ne pas utiliser le Service à des fins de spam ou de harcèlement.',
              'Ne pas tenter de compromettre la sécurité du Service.',
              'Respecter les droits de propriété intellectuelle d\'autrui.'
            ],
            note: 'Vous êtes seul responsable du contenu que vous publiez sur votre profil. IKATone se réserve le droit de supprimer tout contenu contraire aux présentes Conditions.'
          },
          section6: {
            title: '6. Gestion et suppression du compte',
            management: 'Gestion du compte',
            managementText: 'Vous pouvez à tout moment modifier les informations de votre compte et de vos profils depuis votre tableau de bord. Vous pouvez également masquer temporairement vos profils sans les supprimer.',
            deletion: 'Suppression du compte',
            deletionIntro: 'Vous pouvez supprimer votre compte de deux manières :',
            deletionList: [
              'En supprimant vos profils directement depuis votre tableau de bord.',
              'En nous contactant pour demander la suppression complète de votre compte et de vos données.'
            ],
            deletionNote: 'La suppression du compte est définitive. Une fois supprimé, votre compte et vos données ne pourront pas être récupérés.',
            expiration: 'Expiration de l\'abonnement',
            expirationText: 'En cas d\'expiration de votre abonnement, votre compte et vos profils ne sont pas automatiquement supprimés. Vos profils peuvent devenir inactifs ou invisibles, mais vos données sont conservées. Vous pouvez réactiver votre compte en renouvelant votre abonnement.'
          },
          section7: {
            title: '7. Suspension ou résiliation du service',
            intro: 'IKATone se réserve le droit de suspendre ou de résilier votre accès au Service, sans préavis, dans les cas suivants :',
            list: [
              'Violation des présentes Conditions d\'utilisation.',
              'Publication de contenu illégal ou inapproprié.',
              'Utilisation frauduleuse du Service.',
              'Non-paiement des frais dus.',
              'Demande des autorités compétentes.',
              'Tout comportement portant atteinte à IKATone ou à d\'autres utilisateurs.'
            ],
            note: 'En cas de résiliation pour violation des Conditions, aucun remboursement ne sera accordé.'
          },
          section8: {
            title: '8. Limitation de responsabilité',
            intro: 'Le Service IKATone est fourni « tel quel » et « selon disponibilité ». Dans les limites permises par la loi applicable :',
            list: [
              'IKATone ne garantit pas que le Service sera ininterrompu, sécurisé ou exempt d\'erreurs.',
              'IKATone décline toute responsabilité pour les dommages indirects, accessoires, spéciaux ou consécutifs découlant de l\'utilisation du Service.',
              'IKATone n\'est pas responsable du contenu publié par les utilisateurs sur leurs profils.',
              'IKATone n\'est pas responsable des pertes de données résultant de circonstances hors de son contrôle.',
              'La responsabilité totale d\'IKATone est limitée au montant que vous avez payé pour le Service au cours des 12 derniers mois.'
            ],
            note: 'Vous utilisez le Service à vos propres risques et êtes seul responsable de l\'exactitude des informations que vous publiez.'
          },
          section9: {
            title: '9. Propriété intellectuelle',
            p1: 'Le Service IKATone, y compris son design, ses fonctionnalités, son code source, ses textes, images et logos, est la propriété exclusive d\'IKATone et est protégé par les lois sur la propriété intellectuelle.',
            p2: 'Vous n\'êtes pas autorisé à :',
            list: [
              'Copier, modifier ou distribuer le contenu du Service sans autorisation.',
              'Utiliser les marques, logos ou noms commerciaux d\'IKATone sans accord préalable.',
              'Procéder à l\'ingénierie inverse du Service.'
            ],
            p3: 'Vous conservez la propriété du contenu que vous publiez sur vos profils. En utilisant le Service, vous accordez à IKATone une licence limitée pour afficher ce contenu dans le cadre du fonctionnement du Service.'
          },
          section10: {
            title: '10. Droit applicable',
            p1: 'Les présentes Conditions sont régies et interprétées conformément aux lois du Canada. Tout litige relatif au Service sera soumis à la compétence exclusive des tribunaux canadiens.',
            p2: 'Si une disposition des présentes Conditions est jugée invalide ou inapplicable, les autres dispositions resteront pleinement en vigueur.'
          },
          section11: {
            title: '11. Contact',
            intro: 'Pour toute question concernant ces Conditions d\'utilisation, vous pouvez nous contacter :',
            department: 'IKATone - Service Client'
          }
        }
      },
      // Errors
      errors: {
        profileInactive: 'Ce profil est temporairement inactif. Veuillez renouveler l\'abonnement.',
        profileNotFound: 'Profil non trouvé',
        unauthorized: 'Veuillez vous connecter pour continuer',
        serverError: 'Une erreur est survenue. Veuillez réessayer.',
        adminRequired: 'Accès administrateur requis',
        orderFailed: 'Échec de la création de commande. Veuillez réessayer.',
        saveFailed: 'Échec de la sauvegarde. Veuillez réessayer.',
        operationFailed: 'Opération échouée. Veuillez réessayer.',
        invalidImageType: 'Veuillez télécharger un fichier image',
        imageTooLarge: 'L\'image doit faire moins de 5 Mo',
        uploadFailed: 'Échec du téléchargement de l\'image',
        sigleRequired: 'Le sigle business est requis',
        sigleNotAvailable: 'Ce sigle n\'est pas disponible',
        emailRequired: 'L\'email est requis',
        invalidEmail: 'Adresse email invalide',
        passwordRequired: 'Le mot de passe est requis',
        passwordLength: 'Le mot de passe doit contenir au moins 6 caractères',
        firstNameRequired: 'Le prénom est requis',
        lastNameRequired: 'Le nom est requis'
      },
      // Owner Dashboard
      owner: {
        dashboard: 'Mon Tableau de Bord',
        welcomeBack: 'Bon retour',
        backToSite: 'Retour au site',
        buyMoreCards: 'Acheter Plus de Cartes',
        businessAccount: 'Compte Business',
        sigleValidated: 'Sigle validé - Vos profils sont actifs',
        siglePending: 'Sigle en attente de validation par l\'admin',
        businessExplanation: 'Les profils BUSINESS sont conçus pour les entreprises et les équipes. Tous vos membres d\'équipe partagent la même identité professionnelle.',
        profileUrlFormat: 'Format URL des profils',
        totalProfiles: 'Total Profils',
        activeProfiles: 'Profils Actifs',
        totalCards: 'Total Cartes',
        totalOrders: 'Total Commandes',
        totalViews: 'Total Vues',
        views: 'vues',
        profileAnalytics: 'Analytics des Profils',
        avgViewsPerProfile: 'Moy. par Profil',
        topProfiles: 'Profils les Plus Performants',
        recentVisitors: 'Visiteurs Recents',
        viewsToday: "vue(s) aujourd'hui",
        myProfiles: 'Mes Profils',
        noProfiles: 'Pas encore de profils',
        getStarted: 'Commencer',
        noJobTitle: 'Pas de titre',
        subscriptionActive: 'Abonnement actif',
        subscriptionExpired: 'Abonnement expiré',
        profileVisible: 'Visible',
        profileHidden: 'Masqué',
        renewToActivate: 'Renouvelez l\'abonnement pour activer ce profil',
        renew: 'Renouveler',
        subscriptionSummary: 'Résumé Abonnements',
        activeSubscriptions: 'Abonnements Actifs',
        expiredSubscriptions: 'Abonnements Expirés',
        renewalCost: 'Coût de renouvellement',
        year: 'an',
        manageSubscriptions: 'Gérer les Abonnements',
        recentOrders: 'Commandes Récentes',
        noOrders: 'Pas encore de commandes',
        editProfile: 'Modifier le Profil',
        viewPublicProfile: 'Voir le Profil Public',
        profilePhoto: 'Photo de Profil',
        uploadPhoto: 'Télécharger une Photo',
        photoRequirements: 'JPG, PNG ou GIF. Max 5Mo.',
        profileBanner: 'Bannière du Profil',
        selectColor: 'Couleur de fond',
        uploadBannerImage: 'Ou uploader une image personnalisée',
        uploadImage: 'Choisir une image',
        bannerUpdated: 'Bannière mise à jour',
        basicInfo: 'Informations de Base',
        firstName: 'Prénom',
        lastName: 'Nom',
        jobTitle: 'Titre du Poste',
        company: 'Entreprise',
        bio: 'Bio',
        bioPlaceholder: 'Parlez-nous de vous...',
        contactInfo: 'Informations de Contact',
        phone: 'Téléphone Mobile',
        officePhone: 'Téléphone Bureau',
        email: 'Email',
        website: 'Site Web',
        socialLinks: 'Liens Sociaux',
        youtubeChannel: 'Chaîne YouTube',
        youtubeVideo: 'Vidéo YouTube',
        profileSaved: 'Profil enregistré avec succès',
        photoUploaded: 'Photo téléchargée avec succès',
        profileActivated: 'Profil activé',
        profileDeactivated: 'Profil désactivé',
        // New translations for features A-G
        currentPlan: 'Plan Actuel',
        cardsLimit: 'Limite de Cartes',
        expiresOn: 'Expire le',
        generateEditLink: 'Générer le lien d\'édition',
        editLinkCopied: 'Lien d\'édition copié dans le presse-papiers !',
        editLinkExpires: 'Le lien expire le {{date}}',
        toggleDisabledPending: 'Modification désactivée jusqu\'à validation du paiement',
        hideProfile: 'Cacher le profil',
        showProfile: 'Afficher le profil',
        myCards: 'Mes Cartes',
        noCards: 'Aucune carte',
        sortNewest: 'Plus récent',
        sortOldest: 'Plus ancien',
        sortMostViewed: 'Plus consulté',
        sortSigle: 'Sigle (numérique)'
      },
      // Admin Dashboard
      admin: {
        dashboard: 'Tableau de Bord Admin',
        manageEverything: 'Gérez les utilisateurs, profils et commandes',
        totalUsers: 'Total Utilisateurs',
        totalProfiles: 'Total Profils',
        activeProfiles: 'Profils Actifs',
        paidProfiles: 'Profils Payés',
        pendingSigles: 'Sigles en Attente',
        pendingOrders: 'Commandes en Attente',
        tabOverview: 'Aperçu',
        tabUsers: 'Utilisateurs',
        tabProfiles: 'Profils',
        tabOrders: 'Commandes',
        searchPlaceholder: 'Rechercher utilisateurs, profils...',
        pendingSigleValidation: 'Sigles en Attente de Validation',
        pendingPayments: 'Paiements en Attente',
        validate: 'Valider',
        reject: 'Rejeter',
        markPaid: 'Marquer Payé',
        allCaughtUp: 'Tout est à jour !',
        noPendingActions: 'Aucune action en attente pour le moment.',
        allUsers: 'Tous les Utilisateurs',
        email: 'Email',
        name: 'Nom',
        type: 'Type',
        sigle: 'Sigle',
        profiles: 'Profils',
        created: 'Créé le',
        allProfiles: 'Tous les Profils',
        profileId: 'ID Profil',
        owner: 'Propriétaire',
        subscription: 'Abonnement',
        status: 'Statut',
        actions: 'Actions',
        active: 'Actif',
        expired: 'Expiré',
        visible: 'Visible',
        hidden: 'Masqué',
        allOrders: 'Toutes les Commandes',
        orderId: 'ID Commande',
        product: 'Produit',
        quantity: 'Quantité',
        total: 'Total',
        date: 'Date',
        sigleValidated: 'Sigle validé avec succès',
        sigleRejected: 'Sigle rejeté',
        orderMarkedPaid: 'Commande marquée comme payée',
        profileActivated: 'Profil activé',
        profileDeactivated: 'Profil désactivé',
        // New translations for features A-G
        currentPlan: 'Plan Actuel',
        cardsLimit: 'Limite de Cartes',
        expiresOn: 'Expire le',
        generateEditLink: 'Générer le lien d\'édition',
        editLinkCopied: 'Lien d\'édition copié dans le presse-papiers !',
        editLinkExpires: 'Le lien expire le {{date}}',
        toggleDisabledPending: 'Modification désactivée jusqu\'à validation du paiement',
        hideProfile: 'Cacher le profil',
        showProfile: 'Afficher le profil'
      },
      // Products Page (New)
      products: {
        title: 'Choisissez Votre Carte',
        subtitle: 'Cartes intelligentes premium pour chaque professionnel',
        cards: 'cartes',
        card: 'carte',
        freeCard: 'Carte gratuite',
        profile: 'profil',
        selectColor: 'Choisir la Couleur',
        quantity: 'Quantité',
        month: 'mois',
        year: 'an',
        choosePlan: 'Choisissez Votre Plan',
        businessSigle: 'Acronyme Business',
        sigleRequired: 'L\'acronyme business est requis',
        sigleTooLong: 'L\'acronyme doit faire 8 caractères max',
        sigleInvalid: 'Uniquement lettres et chiffres autorisés',
        sigleHelp: 'Vos profils seront : SIGLE0001, SIGLE0002... (en attente de validation admin)',
        businessMinQuantity: 'Le plan BUSINESS nécessite minimum 10 cartes',
        includes: 'Ce qui est Inclus',
        feature1: 'Carte intelligente premium',
        feature2: 'Abonnement PRO annuel',
        feature2Free: '1ère année d\'abonnement OFFERTE',
        feature3: 'Mises à jour profil illimitées',
        feature4: 'Statistiques de vues',
        orderSummary: 'Résumé de Commande',
        annualPlan: 'Plan Annuel',
        total: 'Total',
        firstYear: 'Abonnement première année',
        firstYearFree: '1ère année OFFERTE',
        firstYearFreeLabel: '1ère année d\'abonnement OFFERTE',
        renewalYear2: 'Renouvellement (Année 2+)',
        proceedToCheckout: 'Passer à la Caisse',
        createAccount: 'Créez Votre Compte',
        placeOrder: 'Passer la Commande',
        paymentNote: 'Le paiement est traité manuellement. Vous serez contacté pour finaliser le paiement.',
        orderCreated: 'Commande créée avec succès !',
        contactInfo: 'Vos Informations',
        stripeInfoNote: 'Votre compte sera créé automatiquement après le paiement. Vous recevrez vos identifiants de connexion par email.',
        buyFromDashboard: 'Acheter depuis le Dashboard',
        getStarted: 'Commencer',
        readyToStart: 'Prêt à commencer ?',
        readyToStartDesc: 'Créez votre carte de visite digitale en quelques minutes.',
        secure: 'Paiement sécurisé',
        instant: 'Activation instantanée',
        premium: 'Qualité premium'
      },
      // Subscriptions Page
      subscriptions: {
        title: 'Gérer les Abonnements',
        subtitle: 'Renouvelez vos abonnements pour garder vos profils actifs',
        activeProfiles: 'Actifs',
        expiringSoon: 'Expire Bientôt',
        expiredProfiles: 'Expirés',
        selectProfiles: 'Sélectionnez les profils à renouveler',
        selectExpired: 'Sélectionner Expirés',
        selectAll: 'Tout Sélectionner',
        clearAll: 'Effacer',
        noProfiles: 'Aucun profil à afficher',
        expired: 'Expiré',
        daysLeft: 'jours restants',
        active: 'Actif',
        renewalSummary: 'Résumé du Renouvellement',
        profilesSelected: 'Profils sélectionnés',
        pricePerProfile: 'Prix par profil',
        year: 'an',
        total: 'Total',
        annualSubscription: 'Abonnement annuel (12 mois)',
        requestRenewal: 'Demander le Renouvellement',
        paymentNote: 'Vous serez contacté pour finaliser votre paiement.',
        renewalSubmitted: 'Demande de renouvellement soumise avec succès !',
        pendingPayments: 'Paiements en Attente',
        awaitingPayment: 'En attente de paiement',
        profilesLabel: 'profils',
        profilesConcerned: 'Profils concernés',
        createdAt: 'Créé le',
        contactForPayment: 'Veuillez nous contacter pour finaliser votre paiement et activer vos profils.',
        renewalRequestSent: 'Demande de Renouvellement Envoyée',
        orderId: 'ID Commande',
        awaitingAdminValidation: 'Votre demande est en attente de validation. Vous serez contacté pour le paiement.',
        subtotal: 'Sous-total'
      },
      // Account Status
      account: {
        pendingTitle: 'Compte en Attente de Validation',
        pendingMessage: 'Votre compte est en attente de validation. Vous recevrez un email dès qu\'il sera approuvé.',
        loggedInAs: 'Connecté en tant que',
        pendingOrders: 'Commandes en Attente',
        whatToExpect: 'À quoi s\'attendre',
        activationEmail: 'Vous recevrez un email lorsque votre compte sera activé et prêt à utiliser.',
        checkStatus: 'Vérifier le Statut'
      },
      // Checkout Pages
      checkout: {
        processing: 'Traitement du Paiement',
        pleaseWait: 'Veuillez patienter pendant que nous confirmons votre paiement...',
        paymentSuccessful: 'Paiement Réussi !',
        thankYou: 'Merci pour votre achat. Votre carte IKATone sera expédiée bientôt.',
        amountPaid: 'Montant Payé',
        downloadInvoice: 'Télécharger la Facture',
        goDashboard: 'Aller au Tableau de Bord',
        orderCreated: 'Commande Créée !',
        orderPendingTitle: 'Commande en Attente de Validation',
        orderPendingMessage: 'Votre commande a été créée et est en attente de validation. Vous recevrez un email dès qu\'elle sera approuvée.',
        pendingPaymentMessage: 'Votre commande a été créée. Complétez le paiement pour activer vos profils.',
        orderNumber: 'Numéro de Commande',
        totalAmount: 'Montant Total',
        profilesCreated: 'Profils Créés',
        firstYearFreeNote: '1ère Année d\'Abonnement OFFERTE',
        nextSteps: 'Prochaines Étapes',
        nextStepsDetails: 'Complétez votre paiement via Stripe, ou contactez-nous pour un paiement manuel. Vos profils seront activés une fois le paiement confirmé.',
        whatHappensNext: 'Que se passe-t-il ensuite ?',
        pendingStep1: 'Notre équipe examinera votre commande et vous contactera pour le paiement.',
        pendingStep2: 'Une fois le paiement confirmé, votre compte sera activé.',
        pendingStep3: 'Vous recevrez un email avec vos identifiants de connexion et accès au tableau de bord.',
        emailConfirmation: 'La confirmation sera envoyée à',
        pendingNotice: 'Veuillez conserver ce numéro de commande. Vous en aurez besoin pour finaliser votre paiement.',
        contactUs: 'Nous Contacter',
        sessionExpired: 'Session Expirée',
        sessionExpiredMessage: 'Votre session de paiement a expiré. Veuillez réessayer.',
        paymentIssue: 'Problème de Paiement',
        paymentIssueMessage: 'Un problème est survenu lors du traitement de votre paiement. Veuillez réessayer ou contacter le support.',
        tryAgain: 'Réessayer',
        contactSupport: 'Contacter le Support',
        paymentCancelled: 'Paiement Annulé',
        paymentCancelledMessage: 'Vous avez annulé le paiement. Votre commande a été sauvegardée.',
        orderSavedMessage: 'Vous pouvez finaliser votre paiement à tout moment depuis votre tableau de bord.',
        retryPayment: 'Réessayer le Paiement',
        goToDashboardToPay: 'Aller au Tableau de Bord pour Payer',
        needHelp: 'Besoin d\'Aide ?',
        promoCode: 'Code promo',
        promoPlaceholder: 'Entrez votre code',
        apply: 'Appliquer',
        couponApplied: 'Code promo appliqué avec succès',
        couponInvalid: 'Code promo invalide ou expiré'
      },
      // Settings Pages
      settings: {
        title: 'Paramètres',
        adminTitle: 'Paramètres Admin',
        accountInfo: 'Informations du Compte',
        email: 'Email',
        name: 'Nom',
        plan: 'Plan',
        role: 'Rôle',
        changePassword: 'Changer le Mot de Passe',
        currentPassword: 'Mot de Passe Actuel',
        newPassword: 'Nouveau Mot de Passe',
        confirmPassword: 'Confirmer le Mot de Passe',
        updatePassword: 'Mettre à Jour le Mot de Passe',
        passwordChanged: 'Mot de passe modifié avec succès !',
        passwordsNoMatch: 'Les nouveaux mots de passe ne correspondent pas'
      },
      // Profile Edit (Secure Link)
      profileEdit: {
        title: 'Modifier Votre Profil',
        subtitle: 'Mettez à jour vos informations de profil public',
        invalidLink: 'Ce lien est invalide ou a expiré',
        linkExpired: 'Lien Expiré',
        expiresOn: 'Le lien expire le',
        profileId: 'ID du Profil',
        saveChanges: 'Enregistrer les Modifications',
        saved: 'Profil mis à jour avec succès !',
        success: 'Profil Mis à Jour !',
        successMessage: 'Vos modifications ont été enregistrées. Vous pouvez maintenant fermer cette page.',
        viewProfile: 'Voir Mon Profil'
      },
      // Buy More Cards
      buyCards: {
        title: 'Acheter Plus de Cartes',
        subtitle: 'Ajoutez de nouvelles cartes à votre abonnement existant',
        limitReached: 'Limite de Cartes Atteinte',
        currentPlan: 'Plan Actuel',
        cardsUsed: 'cartes utilisées',
        backToDashboard: 'Retour au Tableau de Bord',
        expiresOn: 'L\'abonnement expire le',
        selectCard: 'Sélectionner le Type de Carte',
        subscriptionIncluded: 'Abonnement 1ère année inclus',
        proratedInfo: 'Prix au Prorata',
        cards: 'Cartes',
        subscription: 'Abonnement',
        months: 'mois',
        subscriptionFree: '1ère année d\'abonnement OFFERTE'
      }
    }
  }
};

// Initialize i18n with localStorage persistence
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'ikatone_language'
    }
  });

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(i18n.language?.split('-')[0] || 'en');

  useEffect(() => {
    // Ensure language is stored in localStorage
    const storedLang = localStorage.getItem('ikatone_language');
    if (!storedLang) {
      localStorage.setItem('ikatone_language', 'en');
      i18n.changeLanguage('en');
      setLanguage('en');
    }

    const handleLanguageChange = (lng) => {
      const lang = lng.split('-')[0];
      setLanguage(lang);
      localStorage.setItem('ikatone_language', lang);
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    return () => i18n.off('languageChanged', handleLanguageChange);
  }, []);

  const changeLanguage = (lng) => {
    const lang = lng.split('-')[0];
    i18n.changeLanguage(lang);
    localStorage.setItem('ikatone_language', lang);
    setLanguage(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export { useTranslation };
